// Fila de totales de una subtabla de líneas.
//
// Es la hermana pequeña de `TableTotalsFooter`: mismo lenguaje visual (la Σ, el
// recuento en gris, una línea por divisa) pero devuelve un `<tr>` suelto en vez
// de un `<tfoot>` pegado al borde inferior. Las dos cosas son a propósito:
//
//   · un pie flotante dentro de un desplegable no tiene a qué pegarse, y taparía
//     las líneas de abajo al hacer scroll de la tabla maestra;
//   · en Pedidos de compra todas las bandas viven en un mismo `<tbody>`, así que
//     el total de cada una tiene que poder insertarse como una fila más.
//
// Lo que sí se comparte es el CÁLCULO (`subtotales` → `computeAggregations`), que
// es donde estaría el riesgo de que dos totales de la misma app no cuadren.
import { useTranslation } from 'react-i18next'
import type { AggResult } from '@/types'
import { formatAggValue } from '@/utils/columnAggregations'
import { subtotales, type ColumnaPie } from '@/utils/subtotalesLineas'

interface Props<T> {
  dark: boolean
  /** Las filas de ESTE bloque, no las del pedido entero. */
  rows: T[]
  /** Una entrada por columna visible, en el mismo orden que la cabecera. */
  columnas: ColumnaPie<T>[]
  /** Filas que habría sin filtrar. Con ella el pie dice «4 de 52» en vez de
   *  «4», que es la diferencia entre creer que el pedido tiene cuatro líneas y
   *  saber que se están viendo cuatro de cincuenta y dos. */
  totalSinFiltrar?: number
}

export function SubTotalsRow<T>({ dark, rows, columnas, totalSinFiltrar }: Props<T>) {
  const { t } = useTranslation()

  const resultados = subtotales(rows, columnas)
  const primera = columnas.findIndex(c => resultados[c.key])
  // Sin ninguna columna que sumar no hay fila que pintar: mejor nada que una
  // banda vacía repetida bajo cada factura.
  if (primera === -1 || rows.length === 0) return null

  const celda = `px-2 py-1.5 text-[11px] whitespace-nowrap border-t ${
    dark
      ? 'bg-white/[0.05] border-white/15 text-slate-100'
      : 'bg-slate-100/80 border-slate-300 text-slate-800'
  }`
  const mutedTxt = dark ? 'text-slate-400' : 'text-slate-500'

  return (
    <tr>
      {primera > 0 && (
        <td colSpan={primera} className={`${celda} font-semibold`}>
          <span className="text-sky-500">Σ</span>{' '}
          <span className={`font-normal ${mutedTxt}`}>
            {/* Con filtro puesto, «4» a secas se lee como «el pedido tiene
                cuatro líneas». Decir «4 de 52» es la diferencia entre creerlo y
                saber lo que se está viendo. */}
            {totalSinFiltrar !== undefined && totalSinFiltrar !== rows.length
              ? t('common.agg.linesOf', {
                  count: rows.length,
                  formattedCount: rows.length.toLocaleString('es-ES'),
                  total: totalSinFiltrar.toLocaleString('es-ES'),
                })
              : t('common.agg.lines', {
                  count: rows.length,
                  formattedCount: rows.length.toLocaleString('es-ES'),
                })}
          </span>
        </td>
      )}

      {columnas.slice(primera).map(col => {
        const res: AggResult | undefined = resultados[col.key]
        if (!res) return <td key={col.key} className={celda} />

        const escribir = (valor: number) =>
          col.format
            ? col.format(valor)
            : formatAggValue(valor, 'sum', col.agg?.decimals ?? 0)

        return (
          <td key={col.key} className={`${celda} text-right tabular-nums font-semibold`}>
            {res.kind === 'number' ? (
              // Sin valores que agregar se pinta un guion, no un cero: el cero
              // afirmaría que la métrica vale cero cuando lo que pasa es que no
              // hay dato. Mismo criterio que las celdas y que el pie de arriba.
              res.count === 0
                ? <span className={mutedTxt}>—</span>
                : escribir(res.value)
            ) : res.parts.length === 0 ? (
              <span className={mutedTxt}>—</span>
            ) : (
              // Una línea por divisa: sumar EUR con USD daría una cifra sin
              // significado. El código sólo se escribe cuando hace falta para
              // distinguir, igual que en las celdas.
              <span className="flex flex-col items-end leading-tight">
                {res.parts.map(p => (
                  <span key={p.currency}>
                    {escribir(p.value)}
                    {(res.parts.length > 1 || (p.currency && p.currency !== 'EUR')) && (
                      <span className={`ml-1 text-[9px] font-normal ${mutedTxt}`}>
                        {p.currency}
                      </span>
                    )}
                  </span>
                ))}
              </span>
            )}
          </td>
        )
      })}
    </tr>
  )
}
